import {
  IsString,
  IsEnum,
  IsOptional,
  IsArray,
  IsUUID,
  IsDateString,
  MaxLength,
  MinLength,
  ValidateNested,
  IsObject,
} from 'class-validator'
import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger'
import { Type } from 'class-transformer'
import { CampaignType } from '../../../enums'

export class CreateCampaignTargetDto {
  @ApiPropertyOptional({
    description: "ID de l'établissement ciblé",
    example: '770e8400-e29b-41d4-a716-446655440003',
  })
  @IsUUID()
  @IsOptional()
  establishmentId?: string

  @ApiPropertyOptional({
    description: "UAI de l'établissement (identifiant national)",
    example: '0755678A',
  })
  @IsString()
  @IsOptional()
  establishmentUai?: string

  @ApiPropertyOptional({
    description:
      "Email du contact pour recevoir l'email (obligatoire pour EMAIL_CAMPAIGN et MIXED)",
    example: 'admin@lycee-exemple.fr',
  })
  @IsString()
  @IsOptional()
  contactEmail?: string

  @ApiPropertyOptional({
    description: "Nom du contact ou de l'établissement",
    example: 'Lycée Victor Hugo',
  })
  @IsString()
  @IsOptional()
  contactName?: string

  @ApiPropertyOptional({
    description: 'Rôle du contact (TEACHER, PARENT, ADMIN, etc.)',
    example: 'ADMIN',
  })
  @IsString()
  @IsOptional()
  contactRole?: string

  @ApiPropertyOptional({
    description: "ID de l'utilisateur ciblé (si campagne ciblée)",
    example: 'a1b2c3d4-e5f6-4a5b-8c9d-0e1f2a3b4c5d',
  })
  @IsUUID()
  @IsOptional()
  userId?: string
}

/**
 * DTO for creating a new campaign
 */
export class CreateCampaignDto {
  @ApiProperty({
    description: 'Campaign name',
    example: 'Back to School 2024',
    maxLength: 200,
  })
  @IsString()
  @MinLength(3)
  @MaxLength(200)
  name!: string

  @ApiPropertyOptional({
    description: 'Campaign description',
  })
  @IsString()
  @IsOptional()
  description?: string

  @ApiProperty({
    enum: CampaignType,
    description: 'Type de campagne',
    example: CampaignType.MIXED,
  })
  @IsEnum(CampaignType)
  type!: CampaignType

  @ApiPropertyOptional({
    description: 'Scheduled start date',
    example: '2024-09-01T08:00:00Z',
  })
  @IsDateString()
  @IsOptional()
  scheduledAt?: string

  @ApiPropertyOptional({
    description: "Sujet de l'email (si type EMAIL_CAMPAIGN ou MIXED)",
    example: 'Invitation - Réunion Parents-Professeurs',
  })
  @IsString()
  @IsOptional()
  subject?: string

  @ApiPropertyOptional({
    description: 'ID du template à utiliser',
  })
  @IsUUID()
  @IsOptional()
  templateId?: string

  @ApiPropertyOptional({
    description: 'Données pour le template (JSON)',
    example: { eventDate: '15/03/2025', eventTime: '18h00' },
  })
  @IsOptional()
  @IsObject()
  templateData?: Record<string, any>

  @ApiProperty({
    description: 'Liste des cibles de la campagne',
    type: [CreateCampaignTargetDto],
    example: [
      { establishmentId: 'uuid-1', contactEmail: 'contact@school1.fr' },
      { establishmentId: 'uuid-2', contactEmail: 'contact@school2.fr' },
    ],
  })
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => CreateCampaignTargetDto)
  targets!: CreateCampaignTargetDto[]

  @ApiPropertyOptional({
    description: 'Legacy field for establishment ID compatibility',
  })
  @IsUUID()
  @IsOptional()
  establishmentId?: string

  @ApiPropertyOptional({
    type: [String],
    description: 'Legacy field for target roles compatibility',
    example: ['teacher', 'student'],
  })
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  targetRoles?: string[]

  @ApiPropertyOptional({
    type: [String],
    description: 'Legacy field for target user IDs compatibility',
  })
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  targetUserIds?: string[]

  @ApiPropertyOptional({
    description: 'Legacy field for endsAt compatibility',
    example: '2024-09-15T23:59:59Z',
  })
  @IsDateString()
  @IsOptional()
  endsAt?: string
}
